/**
 * Componente para mostrar información detallada de una tarea cron
 * Frecuencia, horarios, timezone, etc.
 */
import React, { useMemo } from 'react'
import { ClockIcon, CalendarIcon } from '@heroicons/react/24/outline'
import type { CronTaskStatusData } from '../../types/cron/cronTasks'
import { formatFlexyLastSyncedAt } from '../../utils/flexyStockUtils'

interface CronTaskInfoProps {
  status: CronTaskStatusData | null
  count: number | null
  showCount: boolean
  countLabel?: string
  lastSyncedAt?: string | null
}

const CronTaskInfo: React.FC<CronTaskInfoProps> = React.memo(
  ({ status, count, showCount, countLabel = 'Pedidos pendientes:', lastSyncedAt }) => {
    const executionHours = useMemo(
      () => status?.executionSchedule?.hours || [],
      [status?.executionSchedule?.hours]
    )

    const formattedHours = useMemo(() => {
      return executionHours
        .map((h) => `${h.toString().padStart(2, '0')}:00`)
        .join(', ')
    }, [executionHours])

    if (!status) {
      return null
    }

    return (
      <div className="space-y-2 text-sm">
        {/* Frecuencia */}
        <div className="flex items-start space-x-2">
          <ClockIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-gray-600">Frecuencia:</span>
            <span className="ml-2 font-medium text-gray-900">
              {status.frequency}
            </span>
          </div>
        </div>

        {/* Horarios de ejecución */}
        {executionHours.length > 0 && (
          <div className="flex items-start space-x-2">
            <CalendarIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-gray-600">Horarios:</span>
              <span className="ml-2 font-medium text-gray-900">
                {formattedHours}
              </span>
            </div>
          </div>
        )}

        {/* Timezone */}
        <div className="flex items-start space-x-2">
          <span className="text-gray-600">Zona horaria:</span>
          <span className="ml-2 font-medium text-gray-900">
            {status.timezone}
          </span>
        </div>

        {lastSyncedAt !== undefined && (
          <div className="flex items-start space-x-2 pt-2 border-t border-gray-200">
            <span className="text-gray-600">Última sync:</span>
            <span className="font-medium text-gray-900">
              {formatFlexyLastSyncedAt(lastSyncedAt)}
            </span>
          </div>
        )}

        {/* Contador (si aplica) */}
        {showCount && count !== null && (
          <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
            <span className="text-gray-600">{countLabel}</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md font-semibold text-sm">
              {count}
            </span>
          </div>
        )}
      </div>
    )
  }
)

CronTaskInfo.displayName = 'CronTaskInfo'

export default CronTaskInfo

